import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {scopes, resources} from '../../../common/permissions/dictionary';
import {paths} from '../../../common/dictionaries/urls';
import {Actions} from '../../../common/actions';
import {Card, Grid, Button, Input, Dropdown, Icon} from 'semantic-ui-react';
import DatePicker from 'react-datepicker';
import {dateFormatPicker, noTimeZoneShortISODate, hourFormat} from '../../../common/constants/datesFormat';
import {DashboardTable} from './dashboardTable';
import {hasAccessPermission} from '../../../common/permissions/validations';
import fscreen from 'fscreen';
import {VIEWS, FILLER_STATUS, useInterval} from './utils';
import {downloadFile} from '../../../common/util/file';
import moment from 'moment';

import './dashboard.scss';

const {
  cleanImportsTrackingInvoicesList,
  fetchImportsTrackingStatusList,
  fetchPartsImportsInvoices,
  exportPartsImportsInvoices,
  cleanExportedPartsImportsListUrl,
  setTimerRequest,
  navigateTo,
} = Actions;


const FILTERS_INITIAL_STATE = {
  'startDate': '',
  'endDate': '',
  'invoice': '',
  'tracking_status': 'ALL',
  'filler_status': FILLER_STATUS.ordered_japan,
};

const Index = () => {
  const permissions = useSelector((state) => state.session.permissions);
  const [selectedFilters, setSelectedFilters] = useState(FILTERS_INITIAL_STATE);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [lastListUpdate, setLastListUpdate] = useState(Date.now());
  const loading = useSelector((state) => state.partsImportsInvoices.loading);
  const partsImportsInvoices = useSelector((state) => state.partsImportsInvoices.list);
  const timerRequest = useSelector((state) => state.partsImportsInvoices.timerRequest);
  const importsTrackingStatusList = useSelector((state) => state.partsImportsInvoices.statusList);
  const [dashboardView, setDashboardView] = useState(VIEWS.imports);
  const [cdActiveTab, setCdActiveTab] = useState(VIEWS.imports);
  const exportedPartsImportsListURL = useSelector((state) => state.partsImportsInvoices.url);
  const dispatch = useDispatch();

  const handler = () => {
    if (!fscreen.fullscreenElement) {
      setIsFullScreen(false);
    }
  };

  const changeFullScreen = () => {
    if (fscreen.fullscreenEnabled) {
      fscreen.addEventListener('fullscreenchange', handler.bind(this), false);
    }
  };

  useEffect(() => {
    setLastListUpdate(Date.now());
  }, [partsImportsInvoices]);

  const searchRequest = () => {
    const filtersData = {...selectedFilters,
      'tracking_status': dashboardView === VIEWS.cd && cdActiveTab === 2 ? 'R' :
        selectedFilters.tracking_status === 'ALL' ? '' : selectedFilters.tracking_status,
      'filler_status': dashboardView === VIEWS.imports ?
        FILLER_STATUS.ordered_japan : FILLER_STATUS.list_bill_ordered};
    if (timerRequest) {
      dispatch(setTimerRequest(false));
      dispatch(fetchPartsImportsInvoices(filtersData));
    }
  };

  useInterval(() => {
    searchRequest();
  }, 2000 * 1000);

  const handleSearchClick = () => {
    searchRequest();
  };

  const cleanFilters = () => {
    setSelectedFilters(FILTERS_INITIAL_STATE);
    dispatch(cleanImportsTrackingInvoicesList());
  };

  useEffect((last, next) => {
    handleSearchClick();
  }, [dashboardView, cdActiveTab]);

  useEffect(() => {
    if (!hasAccessPermission(permissions, resources.PartsImportsTrackingDashboard, scopes.See) &&
      !hasAccessPermission(permissions, resources.PartsImportsTrackingDashboard, scopes.Edit)) {
      dispatch(navigateTo(paths.AccessDenied));
    }
    cleanFilters();
    changeFullScreen();
    dispatch(setTimerRequest(true));
    dispatch(fetchImportsTrackingStatusList());
  }, []);

  const toggleFullScreen = () => {
    if (!fscreen.fullscreenElement) {
      fscreen.requestFullscreen(document.documentElement);
      setIsFullScreen(true);
    } else {
      fscreen.exitFullscreen();
      setIsFullScreen(false);
    }
  };

  const handleOnChangeValues = (value, field) => {
    switch (field) {
      case 'startDate':
        setSelectedFilters({...selectedFilters,
          'startDate': value ? moment(value).format(noTimeZoneShortISODate) : ''});
        break;

      case 'endDate':
        setSelectedFilters({...selectedFilters,
          'endDate': value ? moment(value).format(noTimeZoneShortISODate) : ''});
        break;

      case 'invoice':
        setSelectedFilters({...selectedFilters,
          'invoice': value ? value : ''});
        break;

      case 'tracking_status':
        setSelectedFilters({...selectedFilters,
          'tracking_status': value ? value : ''});
        break;
      default:
        break;
    }
  };

  const getTrackingStatusListOptions = (trackingStatusList) => {
    const options = trackingStatusList.map((item) => {
      return {'text': item.statusName, 'value': item.status};
    });
    options.unshift({'text': 'TODOS', 'value': 'ALL'});
    return options;
  };

  const getDateFormatted = (date) => {
    return date ? moment(date, noTimeZoneShortISODate).toDate() : null;
  };

  const changeDashboardView = () => {
    dashboardView === VIEWS.imports ?
      setDashboardView(VIEWS.cd) : setDashboardView(VIEWS.imports);
  };

  const onTabChange = (event, data) => {
    if (cdActiveTab === data.activeIndex) {
      return '';
    }
    setCdActiveTab(data.activeIndex);
  };

  const handleExportToExcel = (e) => {
    e.preventDefault();
    const filtersData = {...selectedFilters,
      'tracking_status': dashboardView === VIEWS.cd && cdActiveTab === 2 ? 'R' :
        selectedFilters.tracking_status === 'ALL' ? '' : selectedFilters.tracking_status,
      'filler_status': dashboardView === VIEWS.imports ?
        FILLER_STATUS.ordered_japan : FILLER_STATUS.list_bill_ordered};
    dispatch(exportPartsImportsInvoices(filtersData));
  };

  useEffect(() => {
    if (exportedPartsImportsListURL) {
      downloadFile(exportedPartsImportsListURL);
      dispatch(cleanExportedPartsImportsListUrl());
    }
  }, [exportedPartsImportsListURL]);

  const icon = isFullScreen ? 'compress' : 'expand';

  return (
    <div>
      <Card raised fluid>
        <div className='title-bar'>
          <Grid columns={2} stackable>
            <Grid.Row>
              <Grid.Column>
                <div className='title'>
                  <h2>Importaciones de repuestos</h2>
                </div>
              </Grid.Column>
              <Grid.Column textAlign='right' className='flex middle center'>
                <Icon link name={icon} size='big'
                  onClick={toggleFullScreen.bind(this)} />
                <Button secondary size='small' className='add-btns-pading' compact
                  onClick={(e) => handleExportToExcel(e)}>
                  <img src={`${process.env.PUBLIC_URL}/icons/excel.svg`} alt='excel icon' width={25} />
                </Button>
                <Button primary onClick={() => changeDashboardView()}>{
                  dashboardView === VIEWS.imports ? 'Centro de Distribución' : 'Importaciones'
                }</Button>
                <Button primary onClick={cleanFilters.bind(this)}>Limpiar</Button>
                <Button primary onClick={handleSearchClick.bind(this)}>Buscar</Button>
              </Grid.Column>
            </Grid.Row>
            <label style={{'marginTop': '-20px'}}>
              Última actualización: {moment(lastListUpdate).format(hourFormat)}</label>
          </Grid>
        </div>
        <div className='filters-section'>
          <Grid columns={4} stackable>
            <Grid.Row>
              <Grid.Column>
                <div className='flex middle'>
                  {<label><h4>Desde:</h4></label>}
                  <DatePicker locale='es' dateFormat={dateFormatPicker}
                    disabled={loading} todayButton='Hoy'
                    customInput={<Input readOnly icon='calendar' size='small' />}
                    selected={getDateFormatted(selectedFilters.startDate)}
                    onChange={(value) => handleOnChangeValues(value, 'startDate')}
                  />
                </div>
              </Grid.Column>
              <Grid.Column>
                <div className='flex middle'>
                  {<label><h4>Hasta:</h4></label>}
                  <DatePicker locale='es' dateFormat={dateFormatPicker}
                    disabled={loading} todayButton='Hoy'
                    customInput={<Input readOnly icon='calendar' size='small' />}
                    selected={getDateFormatted(selectedFilters.endDate)}
                    onChange={(value) => handleOnChangeValues(value, 'endDate')}
                  />
                </div>
              </Grid.Column>
              <Grid.Column>
                <div className='flex middle'>
                  {<label><h4>Factura:</h4></label>}
                  <Input placeholder='Nº Factura'
                    value={selectedFilters.invoice}
                    disabled={loading}
                    onChange={(e, {value}) => handleOnChangeValues(value, 'invoice')}
                  />
                </div>
              </Grid.Column>
              <Grid.Column>
                <div className='flex middle'>
                  {<label><h4>Estado:</h4></label>}
                  <Dropdown selection search
                    options={getTrackingStatusListOptions(importsTrackingStatusList)}
                    name='status-container'
                    value={selectedFilters.tracking_status}
                    onChange={(e, {value}) => handleOnChangeValues(value, 'tracking_status')}
                    placeholder='Seleccione Estado'
                    noResultsMessage='Estado no encontrado'
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </Card>
      <DashboardTable partsImportsInvoices={partsImportsInvoices} loading={loading} search={handleSearchClick}
        view={dashboardView} onTabChange={onTabChange} />
    </div>
  );
};

export default Index;
